import React, { useEffect, useState, useRef } from 'react';
// Menu and ArrowLeft no longer used here — mobile menu moved to Sidebar.tsx
// import { Menu, ArrowLeft } from 'lucide-react';
import { Wrench } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { MobileBottomNav } from './components/MobileBottomNav';
import { useUserGroup } from './hooks/useUserGroup';
import { useMaintenanceFlag } from './hooks/useMaintenanceFlag';
import { landingIsV2 } from './lib/userGroups';
import { identifyUser, resetPostHog } from './lib/posthog';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { PermissionProvider } from './context/PermissionContext';
import { MobileBlocker } from './components/MobileBlocker';
import { ScrollToTop } from './components/ScrollToTop';
import { PlaceholderPage } from './pages/PlaceholderPage';
import Welcome from './pages/Welcome';
import { Auth } from './pages/Auth';
import GrabarVoz from './pages/GrabarVoz';
import GenerarAudio from './pages/GenerarAudio';
import GrabarDialogo from './pages/GrabarDialogo';
import { Settings } from './pages/Settings';
import { SalesRegister } from './pages/SalesRegister';
import { DataUpload } from './pages/DataUpload';
import { AutomaticSync } from './pages/AutomaticSync';
import { PriceList } from './pages/PriceList';
import { Discounts } from './pages/Discounts';
import { Stock } from './pages/Stock';
import { Messages } from './pages/Messages';
import { InteractionCharts } from './pages/InteractionCharts';
import { Billing } from './pages/Billing';
import { supabase } from './lib/supabase';
import { Session } from '@supabase/supabase-js';
import { retrySupabase, onCircuitChange, getCircuitState, resetCircuit } from './utils/retry';
// WelcomeModal component is temporarily disabled but kept in codebase
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { UpdateInfoModal } from './components/UpdateInfoModal';
import { GeneratedImagesHistory } from './pages/GeneratedImagesHistory';
import { ExportarTiendanube } from './pages/ExportarTiendanube';
import { Registro } from './pages/Registro';
import { AccountSetupLayout } from './components/onboarding/AccountSetupLayout';
import { ONBOARDING_V2 } from './components/onboarding/onboardingV2Routes';
import { useIsDesktop } from './components/onboarding/useIsDesktop';
import { AccountSetup } from './pages/AccountSetup';
import { AccountSetupCategory } from './pages/AccountSetupCategory';
import { AccountSetupContent } from './pages/AccountSetupContent';
import { AccountSetupProduct } from './pages/AccountSetupProduct';
import { AccountSetupGenerating } from './pages/AccountSetupGenerating';
import { AccountSetupResults } from './pages/AccountSetupResults';
import { EmployeeOnboarding } from './pages/EmployeeOnboarding';
import { OwnerOnboarding } from './pages/OwnerOnboarding';
import { BrandDescription } from './pages/BrandDescription';
import { RevisarMarca } from './pages/RevisarMarca';
import { MarketingPlanChat } from './pages/MarketingPlanChat';
import { MarketingPlanReview } from './pages/MarketingPlanReview';
import { CalendarRouter } from './pages/CalendarRouter';
import { Ayuda } from './pages/Ayuda';
import { Modos } from './pages/Modos';
import { MiMarca } from './pages/MiMarca';
import Paywall from './pages/Paywall';
import { ResetPassword } from './pages/ResetPassword';
import SeleccionarProductosAnuncio from './pages/SeleccionarProductosAnuncio';
import { EditarImagen } from './pages/EditarImagen';
import EdicionGrafica from './pages/edicionGrafica/EdicionGrafica';
import EdicionGraficaLab from './pages/edicionGrafica/EdicionGraficaLab';
import { EditarVideo } from './pages/EditarVideo';
import { EditarVideoIAHistorial } from './pages/EditarVideoIAHistorial';
import GeneratedImagesPage from './pages/GeneratedImagesPage';
import SeleccionarTextoEstudioVirtual from './pages/nuevo_modo/SeleccionarTextoEstudioVirtual';
import { SeleccionarPoseModelo } from './pages/SeleccionarPoseModelo';
import { TestV1 } from './pages/TestV1';
import InterfazV2 from './pages/v2/InterfazV2';
import InterfazV2Plantillas from './pages/v2/InterfazV2Plantillas';
import ColeccionDetalle from './pages/v2/ColeccionDetalle';
import PruebaNuevaInterfaz from './pages/nuevo_modo/PruebaNuevaInterfaz';
import PersonalizarModeloEstudioVirtual from './pages/nuevo_modo/PersonalizarModeloEstudioVirtual';
import AnadirAEscena from './pages/nuevo_modo/AnadirAEscena';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import Checkout from './pages/Checkout';
import EnterpriseCheckout from './pages/EnterpriseCheckout';
import DisenarVideo from './pages/DisenarVideo';
import ConfigurarVideoInfluencer from './pages/ConfigurarVideoInfluencer';
import ConfigurarVideoInfluencerNuevo from './pages/ConfigurarVideoInfluencerNuevo';
import ConfigurarVideoInfluencerDetalle from './pages/ConfigurarVideoInfluencerDetalle';
import ConfigurarVideoInfluencerVideo from './pages/ConfigurarVideoInfluencerVideo';
import ConfigurarVideoPersonalizado from './pages/ConfigurarVideoPersonalizado';
import ConfigurarVideoCinematografico from './pages/ConfigurarVideoCinematografico';
import PlanificadorVideoCinematografico from './pages/PlanificadorVideoCinematografico';
import GeneratedVideoPage from './pages/GeneratedVideoPage';
import VideoEditor from './pages/VideoEditor';
import FacturacionPortal from './pages/FacturacionPortal';
import FacturaDetalle from './pages/FacturaDetalle';
import VideoProjects from './pages/VideoProjects';
import { mergeLastCrearAnuncioState } from './utils/crearAnuncioStorage';
import UICatalog from './pages/UICatalog';
import UICatalog2 from './pages/UICatalog2';
import {
  DesignSystemLayout,
  DSGettingStartedPage,
  DSTypographyPage,
  DSColorsPage,
  DSSpacingPage,
  DSShadowsPage,
  DSRadiusPage,
  DSSemanticColorsPage,
  DSIconsPage,
  dsFirstPagePath,
} from './pages/design-system';
// Preview de pricing SÓLO en desarrollo (ADR-2026-07-29-ruta-preview-paywall-solo-desarrollo). En el build de producción
// `import.meta.env.DEV` es `false`, la rama de la ruta se elimina y este módulo
// queda sin referencias → Rollup lo saca del bundle.
import PricingDevPreview from './pages/PricingDevPreview';
// Preview de estilos de interfaz — SÓLO desarrollo, misma poda que el de pricing
// (ADR-2026-07-30-tres-estilos-interfaz-shells-propios y ADR-2026-07-30-rutas-solo-dev-indice-pastilla). Tres shells distintos sobre el mismo contenido real de Atlas.
import { EstiloMinimal, HomeMinimal } from './pages/estilos';
// Bypass del onboarding para probar en local (ADR-2026-07-30-bypass-dev-onboarding-entrar-app). En producción
// `saltearOnboarding()` es constante `false` y los guards quedan intactos.
import {
  aplicarSkipOnboardingDeLaURL,
  registrarHelperDeConsola,
  saltearOnboarding,
} from './lib/devSkipOnboarding';
import { clasesPageFade, usePageFade } from './hooks/usePageFade';
import { Toaster } from 'sileo';

// Se ejecuta al importar el módulo, antes del primer render: el guard de perfil
// redirige con `window.location.href` y no llegaría a ver un flag escrito en un
// efecto.
aplicarSkipOnboardingDeLaURL();
registrarHelperDeConsola();
// AddToHomescreenPrompt is triggered manually from Settings page


// Using the new Welcome component instead of placeholder

function MaintenanceScreen() {
  const whatsappUrl = 'https://wa.me/5491122635392';
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(whatsappUrl)}`;
  return (
    <div className="min-h-screen bg-[#EFF0F5] flex items-center justify-center p-6">
      <div className="animate-fade-in w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg ring-1 ring-neutral-200 sm:p-10">
        {/* Estado */}
        <div className="inline-flex items-center gap-2 rounded-full bg-warning-soft px-3 py-1">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
          </span>
          <span className="text-[length:var(--text-caption)] font-semibold uppercase tracking-wide text-warning-text">
            En mantenimiento
          </span>
        </div>

        {/* Ícono */}
        <div className="mx-auto mt-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary">
          <Wrench className="h-7 w-7 text-white" strokeWidth={2} />
        </div>

        {/* Título */}
        <h1 className="mt-6 text-[22px] font-semibold leading-tight text-brand-black sm:text-[24px]">
          Disculpá por el inconveniente
        </h1>

        <p className="mt-3 text-[length:var(--text-body)] font-medium text-brand-black">
          Estamos haciendo mejoras en los servidores.
        </p>
        <p className="mt-1.5 text-[length:var(--text-body)] text-neutral-600">
          Volvé en unos minutos y vas a poder seguir generando con normalidad.
        </p>

        <div className="my-7 h-px bg-neutral-200" />

        {/* Contacto */}
        <p className="text-[length:var(--text-body)] text-neutral-600">
          ¿No podés esperar? Escribinos y te contamos en cuánto estará todo listo.
        </p>

        {/* Mobile: botón WhatsApp */}
        <div className="mt-5 sm:hidden">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2"
          >
            Escribinos por WhatsApp
          </a>
        </div>

        {/* Desktop: QR */}
        <div className="mt-6 hidden flex-col items-center sm:flex">
          <div className="rounded-md border border-neutral-200 bg-white p-3 shadow-sm">
            <img
              src={qrUrl}
              alt="Código QR para escribirnos por WhatsApp"
              className="h-40 w-40"
            />
          </div>
          <p className="mt-3 text-[length:var(--text-caption)] text-neutral-400">
            Escaneá el código para escribirnos por WhatsApp
          </p>
        </div>
      </div>
    </div>
  );
}

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // State for WelcomeModal is kept but unused while the modal is disabled
  // const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  // State for UpdateInfoModal
  const [showUpdateInfoModal, setShowUpdateInfoModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<any>(null);
  const [userId, setUserId] = useState<string>('');
  // Poller control for update_disponible
  const updateCheckerStartedRef = useRef(false);
  const updateChannelRef = useRef<any>(null);
  const DEBUG_UPDATE = false;

  // Maintenance mode (global) — ÚNICA fuente: ConfigCat (NO Supabase, ADR-135).
  // El hook lee el flag por CDN, sobrevive caídas de Supabase y es fail-open.
  const maintenance = useMaintenanceFlag();
  // Company id cached from profile
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Circuit breaker (503) state. `breakerOpen` = banner; `breakerHardOpen` =
  // varios ciclos de fallo seguidos → pantalla completa (misma que mantenimiento).
  // Ver ADR-136.
  const [breakerOpen, setBreakerOpen] = useState(false);
  const [breakerHardOpen, setBreakerHardOpen] = useState(false);
  const [offline, setOffline] = useState<boolean>(typeof navigator !== 'undefined' ? !navigator.onLine : false);

  useEffect(() => {
    const st = getCircuitState();
    setBreakerOpen(st.open);
    setBreakerHardOpen(st.hard);
    const off = onCircuitChange((s) => {
      setBreakerOpen(s.open);
      setBreakerHardOpen(s.hard);
    });
    return () => { try { off(); } catch {} };
  }, []);

  // Recuperación del hard-open: con la pantalla completa puesta, los children se
  // desmontan y dejan de pegarle a Supabase, así que nada detectaría que volvió.
  // Este probe le pega directo (salteando el breaker) cada 7s; si Supabase
  // responde, `resetCircuit` baja el hard-open y la app vuelve. Ver ADR-136.
  useEffect(() => {
    if (!breakerHardOpen) return;
    let alive = true;
    const probe = async () => {
      try {
        const { error } = await supabase
          .from('config_system_atlas')
          .select('id')
          .limit(1);
        // "Recuperado" = Supabase respondió algo que NO sea 5xx. Una respuesta
        // 2xx (o incluso un 4xx de RLS) significa que el server está vivo; solo un
        // 5xx o un throw de red cuentan como "sigue caído". Evita que la pantalla
        // quede pegada si el probe recibe un error que no es de infraestructura.
        const infraDown = !!error && Number((error as any)?.status) >= 500;
        if (alive && !infraDown) resetCircuit();
      } catch {
        /* throw = problema de red → sigue caído */
      }
    };
    probe();
    const id = window.setInterval(probe, 7000);
    return () => { alive = false; clearInterval(id); };
  }, [breakerHardOpen]);

  useEffect(() => {
    const update = () => setOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const refreshRubroCache = async (uid: string) => {
    try {
      const { data: profile } = await retrySupabase(async () =>
        await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', uid)
          .single()
      );
      const idCompany: string | null = (profile as any)?.id_company ?? null;
      if (!idCompany) return;
      const { data: company } = await retrySupabase(async () =>
        await supabase
          .from('company_profiles')
          .select('rubro')
          .eq('id_company', idCompany)
          .single()
      );
      const rubro = (company as any)?.rubro as string | undefined;
      if (rubro) mergeLastCrearAnuncioState({ rubro });
    } catch {}
  };

  useEffect(() => {
    // Detect if the URL contains an OAuth hash fragment (implicit flow)
    const hasOAuthHash = window.location.hash?.includes('access_token');
    console.log('[Auth Debug] URL:', window.location.href);
    console.log('[Auth Debug] Hash:', window.location.hash?.substring(0, 80));
    console.log('[Auth Debug] hasOAuthHash:', hasOAuthHash);

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('[Auth Debug] getSession result:', session ? `user=${session.user.id}` : 'null');
      setSession(session);
      if (session) {
        identifyUser(session.user.id, session.user.email);
        checkUserProfile(session.user.id);
        refreshRubroCache(session.user.id);
      } else if (!hasOAuthHash) {
        // Only stop loading if there's no pending OAuth redirect to process
        setLoading(false);
      }
      // If hasOAuthHash && !session, keep loading — onAuthStateChange will resolve it
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth Debug] onAuthStateChange:', event, session ? `user=${session.user.id}` : 'null');
      setSession(session);
      if (event === 'SIGNED_IN' && session?.user?.id) {
        identifyUser(session.user.id, session.user.email);
        checkUserProfile(session.user.id);
        refreshRubroCache(session.user.id);
      }
      // Al desloguear cortamos el vínculo con el usuario: si en el mismo
      // navegador entra otra cuenta, sus eventos no se le atribuyen a la
      // anterior.
      if (event === 'SIGNED_OUT') {
        resetPostHog();
      }
      // If we were waiting for OAuth hash processing and it failed, stop loading
      if (hasOAuthHash && !session) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUserProfile = async (userId: string) => {
    try {
      // Store userId for later use with update info modal
      setUserId(userId);
      
      const { data: profile, error } = await retrySupabase(async () =>
        await supabase
          .from('profiles')
          .select('name, id_company, home_message, home_message_seen')
          .eq('id', userId)
          .single()
      );

      if (error) {
        console.error('Error fetching profile:', error);
        setLoading(false);
        return;
      }

      // Resolve canonical company UUID (id_company)
      const idCompany: string | null = (profile as any)?.id_company ?? null;
      setCompanyId(idCompany);

      // Usuario sin empresa (recién registrado por mail o por Google) → el
      // cuestionario de onboarding, que es donde ahora se crea la empresa: al
      // terminar el paso 3, con marca por defecto y país deducido
      // (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real). El nombre ya no ramifica
      // el destino: viene del registro, así que sin empresa siempre se arranca en el
      // paso 1.
      // Bypass dev (ADR-2026-07-30-bypass-dev-onboarding-entrar-app): entrar a la app sin empresa para probar pantallas
      // internas. Ojo: sin `id_company` todo lo que dependa de la empresa viene
      // vacío; es para mirar UI, no para probar flujos con datos.
      if (profile && !idCompany && !saltearOnboarding()) {
        window.location.href = ONBOARDING_V2.setup;
        return;
      }

      // If no profile issues, proceed to dashboard
      setLoading(false);
      
      // Modal de novedades de la app — SOLO para planes pagos
      // (ADR-2026-08-03-novedades-solo-planes-pagos). A alguien en la prueba gratis se
      // le interrumpe la primera sesión para contarle mejoras de un producto que
      // todavía no compró; el momento de esa cuenta es probar, no ponerse al día.
      //
      // La consulta del plan corre sólo cuando HAY mensaje pendiente, así el resto de
      // las sesiones no paga una consulta extra. Falla cerrado: si no se puede
      // resolver el plan, no se muestra (mejor omitir una novedad que mostrársela a
      // quien no corresponde).
      if (profile.home_message && profile.home_message_seen === false && idCompany) {
        const { data: companyPlan } = await supabase
          .from('company_profiles')
          .select('plan')
          .eq('id_company', idCompany)
          .single();
        const plan = (companyPlan as { plan?: string | null } | null)?.plan ?? null;
        if (plan && plan !== 'gratis') {
          // home_message is already a JS object since it's a jsonb column in PostgreSQL
          setUpdateMessage(profile.home_message);
          setShowUpdateInfoModal(true);
        }
      }
    } catch (err) {
      console.error('Error checking user profile:', err);
      setLoading(false);
    }
  };

  // Refresco remoto de la app: cuando `company_profiles.update_disponible` pasa a
  // `true`, esta pestaña se recarga sola para tomar la versión nueva.
  //
  // NO es un poll por intervalo (el comentario viejo decía "periodically" y no era
  // cierto): se entera por tres vías —un chequeo al montar, la suscripción realtime a
  // la fila de la empresa, y un re-chequeo al volver a la pestaña (visibilitychange /
  // focus / online, con 2s de debounce)—. Antes de recargar apaga el flag, best-effort.
  useEffect(() => {
    if (!session || !companyId || updateCheckerStartedRef.current) return;
    updateCheckerStartedRef.current = true;
    let isMounted = true;
    // Handlers for immediate re-checks
    let visibilityHandler: (() => void) | null = null;
    let focusHandler: (() => void) | null = null;
    let onlineHandler: (() => void) | null = null;

    const setupUpdateChecker = async () => {
      try {
        if (DEBUG_UPDATE) console.log('[UpdateCheck] companyId (cached):', companyId);
        if (!isMounted) return;

        const checkAndHandleUpdate = async () => {
          try {
            const { data: companyRow, error: companyErr } = await retrySupabase(async () =>
              await supabase
                .from('company_profiles')
                .select('update_disponible')
                .eq('id_company', companyId)
                .single()
            );
            if (DEBUG_UPDATE && companyErr) console.warn('[UpdateCheck] company select error:', companyErr);
            if (DEBUG_UPDATE) console.log('[UpdateCheck] polled update_disponible:', (companyRow as any)?.update_disponible);
            const updateDisponible = (companyRow as any)?.update_disponible === true;
            if (updateDisponible) {
              // Attempt to reset flag before redirecting (best-effort)
              try {
                await supabase
                  .from('company_profiles')
                  .update({ update_disponible: false })
                  .eq('id_company', companyId);
                if (DEBUG_UPDATE) console.log('[UpdateCheck] reset update_disponible to false');
              } catch (_e) {
                // Ignore errors; proceed with redirect
                if (DEBUG_UPDATE) console.warn('[UpdateCheck] failed to reset flag, proceeding to redirect');
              }
              // Reload current page (keeps route) to apply update without moving tabs
              if (DEBUG_UPDATE) console.log('[UpdateCheck] reloading current page');
              window.location.reload();
            }
          } catch (_e) {
            // Silently ignore
            if (DEBUG_UPDATE) console.warn('[UpdateCheck] polling threw exception', _e);
          }
        };

        // Initial update check. El flag de mantenimiento lo maneja
        // useMaintenanceFlag (ConfigCat), ya no Supabase — ver ADR-135.
        await checkAndHandleUpdate();

        // Realtime subscription for immediate updates
        try {
          // Clean previous channel if any
          if (updateChannelRef.current) {
            try { await supabase.removeChannel(updateChannelRef.current); } catch {}
            updateChannelRef.current = null;
          }
          const channel = supabase
            .channel(`company-update-${companyId}`)
            .on(
              'postgres_changes',
              { event: 'UPDATE', schema: 'public', table: 'company_profiles', filter: `id_company=eq.${companyId}` },
              (payload) => {
                if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime payload:', payload);
                const newRow: any = (payload as any)?.new;
                if (newRow && newRow.update_disponible === true) {
                  // Best-effort reset then redirect
                  (async () => {
                    try {
                      await supabase
                        .from('company_profiles')
                        .update({ update_disponible: false })
                        .eq('id_company', companyId);
                      if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime reset flag to false');
                    } catch {}
                    if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime reload current page');
                    window.location.reload();
                  })();
                }
              }
            )
            .subscribe();
          updateChannelRef.current = channel;
          if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime subscription started for company', companyId);
        } catch (_e) {
          // If realtime fails, polling still works
          if (DEBUG_UPDATE) console.warn('[UpdateCheck] realtime subscription failed', _e);
        }
        // Add event-driven immediate checks
        let running = false;
        let debounceTimer: number | null = null;
        const safeCheck = async () => {
          if (running) return;
          running = true;
          try { await checkAndHandleUpdate(); } finally { running = false; }
        };
        visibilityHandler = () => {
          if (document.visibilityState !== 'visible') return;
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(safeCheck, 2000) as unknown as number;
        };
        focusHandler = () => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(safeCheck, 2000) as unknown as number;
        };
        onlineHandler = () => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(safeCheck, 2000) as unknown as number;
        };
        document.addEventListener('visibilitychange', visibilityHandler);
        window.addEventListener('focus', focusHandler as unknown as EventListener);
        window.addEventListener('online', onlineHandler as unknown as EventListener);
      } catch (_e) {
        // Ignore setup errors
      }
    };

    void setupUpdateChecker();

    return () => {
      isMounted = false;
      updateCheckerStartedRef.current = false;
      // Cleanup realtime channel
      if (updateChannelRef.current) {
        try {
          supabase.removeChannel(updateChannelRef.current);
          if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime channel removed');
        } catch {}
        updateChannelRef.current = null;
      }
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      if (focusHandler) window.removeEventListener('focus', focusHandler as unknown as EventListener);
      if (onlineHandler) window.removeEventListener('online', onlineHandler as unknown as EventListener);
    };
  }, [session, companyId]);

  if (loading) {
    return (
      <>
        {(breakerOpen || offline) && (
          <div className="fixed top-0 left-0 right-0 z-[1000] pointer-events-none">
            <div className="mx-auto max-w-4xl sm:max-w-7xl px-4 pt-2">
              <div className="pointer-events-auto rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm px-3 py-2 shadow-sm text-center">
                {offline ? 'Sin conexión a internet. Verificá tu conexión.' : 'Servidores en mantenimiento. Estarán operativos en unos minutos.'}
              </div>
            </div>
          </div>
        )}
        <div className="min-h-screen bg-[#EFF0F5] flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </>
    );
  }

  if (maintenance || breakerHardOpen) {
    return <MaintenanceScreen />;
  }

  // Usuarios sin sesión van al login clásico. Durante un tiempo entraron por el
  // carrusel de bienvenida nuevo (`/bienvenida`, ADR-693), que hoy está en
  // cuarentena hasta terminar la migración
  // (ADR-2026-07-31-onboarding-nuevo-en-cuarentena).
  if (!session) return <Navigate to="/auth" />;

  return (
    <>
      {(breakerOpen || offline) && (
        <div className="fixed top-0 left-0 right-0 z-[1000] pointer-events-none">
          <div className="mx-auto max-w-4xl sm:max-w-7xl px-4 pt-2">
            <div className="pointer-events-auto rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm px-3 py-2 shadow-sm text-center">
              {offline ? 'Sin conexión a internet. Verificá tu conexión.' : 'Servidores en mantenimiento. Estarán operativos en unos minutos.'}
            </div>
          </div>
        </div>
      )}
      {children}
      {/* WelcomeModal disabled as requested but kept in codebase */}
      {/* <WelcomeModal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} /> */}

      {updateMessage && (
        <UpdateInfoModal
          isOpen={showUpdateInfoModal}
          onClose={() => setShowUpdateInfoModal(false)}
          message={updateMessage}
          userId={userId}
        />
      )}
    </>
  );
};
// Landing inicial de la ruta `/`: decide el destino según el grupo de testeo.
// Solo Grupo B (no-dev) arranca en Interfaz V2; dev y Grupo A van al flujo
// clásico (seleccionar productos). Decide de inmediato con el valor cacheado
// (no bloquea esperando el fetch): así un usuario deslogueado cae en el flujo
// clásico → ProtectedRoute → /auth, sin quedar colgado. Para usuarios
// recurrentes el cache de `useUserGroup` hace el destino correcto al instante.
const HomeRedirect = () => {
  const { isDevTester, grupo } = useUserGroup();
  const to = landingIsV2(isDevTester, grupo)
    ? '/interfaz-v2'
    : '/redes-sociales/crear-anuncio/seleccionar-productos';
  return <Navigate to={to} replace />;
};

// Layout with sidebar on desktop
const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const { sidebarWidth } = useSidebar();
  // Fade de entrada al cambiar de página, para toda la app (ADR-2026-07-31-fade-entre-paginas-toda-app).
  const pageFadeVisible = usePageFade();
  // Track mobile menu state via custom event and compute panel width + viewport
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [panelWidth, setPanelWidth] = React.useState(0);
  const [isMobileView, setIsMobileView] = React.useState(false);
  const [hideMenuButton, setHideMenuButton] = React.useState(false);

  // Listen for hide-menu-button events from child components
  React.useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.hide === 'boolean') {
          setHideMenuButton(detail.hide);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener('hide-menu-button' as any, handler as EventListener);
    return () => window.removeEventListener('hide-menu-button' as any, handler as EventListener);
  }, []);

  React.useEffect(() => {
    const computePanelWidth = () => {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
      const width = Math.min(320, Math.round(vw * 0.82));
      setPanelWidth(width);
    };
    const updateBreakpoint = () => {
      const isMobile = typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches === false : true;
      setIsMobileView(isMobile);
    };
    computePanelWidth();
    updateBreakpoint();
    window.addEventListener('resize', computePanelWidth);
    window.addEventListener('resize', updateBreakpoint);
    return () => {
      window.removeEventListener('resize', computePanelWidth);
      window.removeEventListener('resize', updateBreakpoint);
    };
  }, []);

  React.useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.isMobileMenuOpen === 'boolean') {
          setIsMobileMenuOpen(detail.isMobileMenuOpen);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
    return () => window.removeEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
  }, []);

  // Guard: si el usuario tiene una marca en draft (onboarding interrumpido),
  // redirigir al editor para que termine. Si está en home/dashboard con un draft,
  // sus queries no muestran nada porque filtran is_draft=false → estado roto.
  // Bypass dev (ADR-2026-07-30-bypass-dev-onboarding-entrar-app): con el flag prendido no se rebota al editor, así una
  // cuenta con el alta abandonada a medias sigue siendo usable en local.
  const navigate = useNavigate();
  React.useEffect(() => {
    if (saltearOnboarding()) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .maybeSingle();
        const idCompany = (profile as any)?.id_company as string | undefined;
        if (!idCompany || cancelled) return;
        const { data: drafts } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', idCompany)
          .eq('is_draft', true)
          .order('created_at', { ascending: false })
          .limit(1);
        if (cancelled) return;
        const draftId = (drafts && drafts[0]?.id) as string | undefined;
        if (draftId && !window.location.pathname.startsWith('/onboarding/revisar-marca')) {
          navigate(`/onboarding/revisar-marca/${draftId}`, { replace: true });
        }
      } catch {
        // silencioso
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OLD shell transform animation disabled — mobile menu now uses dropdown in Sidebar.tsx
  // const translateX = isMobileMenuOpen && isMobileView ? Math.max(0, panelWidth + 40 ) : 0;
  // const contentOffsetY = isMobileMenuOpen && isMobileView ? 40 : 0;
  // const depthZ = isMobileMenuOpen && isMobileView ? -200 : 0;
  // const perspectivePx = 1200;
  // const shellStyle = isMobileMenuOpen && isMobileView ? { transform: `perspective(...)`, ... } : { transform: 'none' };
  const shellStyle: React.CSSProperties = { transform: 'none' };

  // Avoid adding extra top padding on full-screen pages like Home ('/')
  const location = useLocation();
  const isRootPath = location.pathname === '/';
  const isCreateFlow = location.pathname.startsWith('/redes-sociales/crear-anuncio');
  const isEditImage = location.pathname.startsWith('/redes-sociales/editar-imagen');
  const isGeneratedImages = location.pathname.startsWith('/redes-sociales/imagenes-generadas');
  const isGeneratedVideo = location.pathname === '/redes-sociales/video-generado';
  const isGraphicEditor = location.pathname.startsWith('/redes-sociales/edicion-grafica');
  // Pages that need special top padding handling
  const isSpecialTopPadding = isRootPath || isCreateFlow || isEditImage || isGeneratedImages || isGeneratedVideo || isGraphicEditor;
  // Pages that should have NO bottom padding (full-bleed) - exclude crear-anuncio to give it bottom space
  const isFullBleedFlow = isRootPath || isCreateFlow || isEditImage || isGeneratedImages || isGeneratedVideo || isGraphicEditor;
  // En PWA standalone con `apple-mobile-web-app-status-bar-style: black-translucent`
  // la status bar pasa a ser transparente y el WebView se extiende detrás. El
  // header mobile del Sidebar ya tiene `paddingTop: env(safe-area-inset-top)`,
  // así que su altura efectiva crece con el inset — el padding-top del main
  // tiene que sumar ese mismo inset para que el contenido no se quede tapado.
  const mainTopPadding = isSpecialTopPadding
    ? 'pt-[calc(env(safe-area-inset-top)+3.5rem)] md:pt-0'
    : 'pt-[calc(env(safe-area-inset-top)+3.5rem+0.75rem)] md:pt-0';

  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const estudioVirtualMobileNoMenu =
    isMobileView &&
    (
      (
        searchParams.get('estudio_virtual') === '1' &&
        (location.pathname === '/redes-sociales/crear-anuncio/personalizar-modelo-estudio-virtual' ||
          location.pathname === '/redes-sociales/crear-anuncio/seleccionar-pose' ||
          location.pathname === '/redes-sociales/crear-anuncio/seleccionar-texto-estudio-virtual')
      ) ||
      location.pathname === '/subscription'
    );

  // Ensure global scroll is not accidentally locked by any overlay/modals
  // Reset body/html overflow on mount and on route changes
  React.useEffect(() => {
    try {
      document.body.style.overflow = 'auto';
      (document.documentElement as HTMLElement).style.overflow = 'auto';
    } catch {
      // noop
    }
  }, [location.pathname]);

  const toggleMobileMenuFromLayout = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    window.dispatchEvent(new CustomEvent('sidebar-mobile-toggle', {
      detail: { isMobileMenuOpen: newState }
    }));
  };

  return (
    <div className="bg-surface-mobile md:bg-[#F3F3F3] min-h-[100svh] md:min-h-screen max-w-[100vw] overflow-x-hidden overflow-y-auto md:overflow-y-hidden flex flex-col">
      <Sidebar />
      {/* Barra de navegación inferior (solo mobile, solo en pantallas-tab). */}
      <MobileBottomNav />
      {/* Sombra de la "parte principal" cayendo sobre la nav bar. Es un
          elemento dedicado y NO la sombra del panel de contenido: esa queda
          tapada porque la sidebar (z-30) está por encima del panel (z-auto).
          Acá replicamos la posición + las MISMAS esquinas redondeadas del
          panel para que la sombra SIGA la curva, con z por encima de la
          sidebar. Transparente y sin eventos: sólo proyecta la sombra hacia
          la izquierda (sobre la nav). */}
      <div
        aria-hidden="true"
        className="hidden md:block fixed top-0 right-0 bottom-0 z-[31] pointer-events-none rounded-tl-[26px] rounded-bl-[26px] shadow-[-14px_0_30px_-18px_rgba(2,6,43,0.16)] transition-[left] duration-300 ease-out"
        style={{ left: sidebarWidth }}
      />
      {/* Esquinas redondeadas del panel (desktop) — pintadas como OVERLAY.
          Antes la curva era un clip-path sobre el propio panel fixed que
          scrollea, y eso obligaba al compositor a enmascarar TODAS las capas
          GPU hijas en cada frame → glitches intermitentes al mostrar
          carruseles pesados (fondos/modelos globales). Ver ADR-574. Cada
          pieza es un cuadrado de 26px cuyo radial-gradient pinta el color
          del fondo exterior (#F3F3F3, mismo del contenedor raíz) fuera de la
          curva y deja transparente el cuarto de círculo del panel. Sin
          clip-path ni border-radius sobre capas compuestas: también evita la
          franja oscura de Windows/D3D que motivó el clip-path original. */}
      <div
        aria-hidden="true"
        className="hidden md:block fixed top-0 z-[31] pointer-events-none w-[26px] h-[26px] transition-[left] duration-300 ease-out"
        style={{
          left: sidebarWidth,
          background:
            'radial-gradient(circle 26px at 26px 26px, transparent 25px, #F3F3F3 26px)',
        }}
      />
      <div
        aria-hidden="true"
        className="hidden md:block fixed bottom-0 z-[31] pointer-events-none w-[26px] h-[26px] transition-[left] duration-300 ease-out"
        style={{
          left: sidebarWidth,
          background:
            'radial-gradient(circle 26px at 26px 0px, transparent 25px, #F3F3F3 26px)',
        }}
      />
      {/* OLD hamburger + arrow close buttons disabled — mobile menu now handled by Sidebar.tsx top bar */}
      {/* Transformable shell to mimic screen sliding right and shrinking */}
      <div
        className="relative z-30 md:z-auto transition-transform duration-500 ease-out transform-gpu bg-surface-mobile md:bg-transparent md:min-h-0"
        style={shellStyle}
      >
        <main
          className={`flex-1 main-content overflow-x-hidden ${mainTopPadding} ${isFullBleedFlow ? 'full-bleed' : ''} bg-surface-mobile md:bg-transparent`}
          style={{ maxWidth: '100vw' }}
        >
          {/* Contenido como panel "encima" de la nav gris. En DESKTOP es un
              marco FIJO al viewport con scroll interno: así las esquinas
              izquierdas redondeadas (la curva, no una línea recta) quedan
              siempre visibles arriba y abajo, y nunca se ve "el final" de la
              página. En mobile es full-width plano con scroll de documento. */}
          {/* El fade de entrada (ADR-2026-07-31-fade-entre-paginas-toda-app) va sobre ESTE div, que ya existe, y no
              sobre un wrapper nuevo: un nodo extra cambiaría el bloque contenedor
              de las alturas en porcentaje de todas las pantallas de adentro. La
              sidebar queda afuera del panel, así que no se desvanece. */}
          <div
            className={`content-panel-clip min-h-full md:min-h-0 md:fixed md:top-0 md:right-0 md:bottom-0 md:overflow-y-auto md:overflow-x-hidden bg-surface-mobile md:bg-surface-content md:transition-[left] md:duration-300 md:ease-out ${clasesPageFade(
              pageFadeVisible,
            )}`}
            style={{ left: isMobileView ? undefined : sidebarWidth }}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

// Resultados del onboarding: SÓLO en desktop se muestran dentro del shell de la app
// (sidebar) con el layout clásico; en mobile es la pantalla previa (grilla 2×2)
// full-screen, sin shell. `AccountSetupResults` ya elige el layout por viewport; acá
// se agrega/omite el shell en consecuencia. `DashboardLayout` va directo (NO bajo
// `ProtectedRoute`) porque el usuario del cuestionario no tiene empresa aún y el guard
// lo rebotaría a `/configuracion-cuenta`; el sidebar degrada bien sin empresa.
const AccountSetupResultsRoute = ({ devPreview = false }: { devPreview?: boolean }) => {
  const isDesktop = useIsDesktop();
  return isDesktop ? (
    <DashboardLayout>
      <AccountSetupResults devPreview={devPreview} />
    </DashboardLayout>
  ) : (
    <AccountSetupResults devPreview={devPreview} />
  );
};

// `/bienvenida` y `/bienvenida/:rubro` → `/auth?mode=signup[&rubro=…]`. Esas URLs se
// publicaron (landings por rubro de ADR-785, PWA instaladas), así que siguen andando.
//
// El **query string original se conserva entero**: si se reemplazara por uno nuevo, un
// `/bienvenida/moda?utm_source=meta&ref=ABC` perdería la campaña y el referido, porque
// `captureAttributionParams()` corre al montar `/auth` y para entonces la URL ya no los
// tendría. Ver ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real.
const BienvenidaCompat = () => {
  const { rubro } = useParams<{ rubro?: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('mode', 'signup');
  if (rubro) params.set('rubro', rubro);
  return <Navigate to={`/auth?${params.toString()}`} replace />;
};

function App() {
  return (
    <MobileBlocker>
      <Toaster position="top-right" theme="light" options={{ fill: '#FAFBFC' }} />
      <Router>
        <ScrollToTop />
        <PermissionProvider>
          <SidebarProvider>
          <Routes>
            {/* Públicas — sin guard ni layout */}
            {/* Entrada ÚNICA. El modo sale de `?mode=` (signup → carrusel; sin param
                → login) y el rubro de la campaña de `?rubro=`. Contrato de URL
                intacto: hay links externos que no controlamos.
                Ver ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real. */}
            <Route path="/auth" element={<Auth />} />
            {/* Compatibilidad: `/bienvenida` estuvo publicada (PWA instaladas, links
                de campaña por rubro). El rubro viaja ahora como query param. */}
            <Route path="/bienvenida" element={<BienvenidaCompat />} />
            <Route path="/bienvenida/:rubro" element={<BienvenidaCompat />} />
            <Route path="/grabar-voz" element={<GrabarVoz />} />
            <Route path="/generar-audio" element={<GenerarAudio />} />
            {/* Grabación del diálogo para el plugin (UXP no accede al micrófono).
                Pública a propósito: la página hace su propio gate y ProtectedRoute
                perdería el ?project_id. Ver ADR-579. */}
            <Route path="/grabar-dialogo" element={<GrabarDialogo />} />
            {/* Registro/login a pantalla completa (mobile). En desktop redirige a
                `/auth`, donde el auth es el panel fijo del split.
                Ver ADR-2026-07-30-registro-pantalla-completa. */}
            <Route path={ONBOARDING_V2.registro} element={<Registro />} />
            {/* Pasos 1–4: rutas hijas de un layout común con header fijo (barra de
                progreso animada) y transición slide/push entre pasos. Ver ADR-736. */}
            <Route path={ONBOARDING_V2.setup} element={<AccountSetupLayout />}>
              <Route index element={<AccountSetup />} />
              <Route path="categoria" element={<AccountSetupCategory />} />
              <Route path="contenido" element={<AccountSetupContent />} />
              <Route path="producto" element={<AccountSetupProduct />} />
            </Route>
            <Route path={ONBOARDING_V2.setupGenerando} element={<AccountSetupGenerating />} />
            {/* Resultados del onboarding: en desktop, dentro del shell (sidebar) con el
                layout clásico; en mobile, la pantalla previa (grilla 2×2) sin shell.
                Lo decide `AccountSetupResultsRoute` por viewport (ver arriba). */}
            <Route path={ONBOARDING_V2.setupResultados} element={<AccountSetupResultsRoute />} />
            <Route path="/employee-onboarding" element={<EmployeeOnboarding />} />
            <Route path="/owner-onboarding" element={<OwnerOnboarding />} />
            {/* Alta + pago enterprise por URL personalizada (una por plan). Pública:
                la página maneja su propio signup/login. Ver ADR. */}
            <Route path="/alta-empresa/:plan" element={<EnterpriseCheckout />} />
            <Route path="/restablecer-clave" element={<ResetPassword />} />
            <Route path="/ui-catalog" element={<UICatalog />} />
            <Route path="/ui-catalog-2" element={<UICatalog2 />} />

            {/* Pricing en modo preview — SÓLO dev (ADR-2026-07-29-ruta-preview-paywall-solo-desarrollo). Entra directo al
                catálogo rediseñado, sin sesión y sin pasar por el onboarding.
                En producción esta ruta no existe: da 404 como cualquier otra. */}
            {import.meta.env.DEV && (
              <Route path="/pricing-dev" element={<PricingDevPreview />} />
            )}

            {/* Resultados del alta CON el upsell de planes — SÓLO dev (ADR-2026-07-31-pantalla-resultados-alta-upsell-hacer).
                Es la pantalla real (`AccountSetupResults`, con su shell en desktop y
                full-screen en mobile), no una maqueta: `devPreview` sólo saltea el
                rebote a `/bienvenida` sin sesión y arranca como si el usuario ya
                hubiera descargado, que es la condición del upsell (ADR-2026-07-29-upsell-planes-salir-alta-condicionado).
                El sheet se abre con "Seguir explorando Atlas", igual que en el flujo
                de verdad. En producción esta ruta no existe. */}
            {import.meta.env.DEV && (
              <Route
                path="/onboarding-pricing-dev"
                element={<AccountSetupResultsRoute devPreview />}
              />
            )}

            {/* Home nueva en modo preview — antes `/estilos/minimal` (ADR-2026-07-30-preview-pasa).
                Pública dentro de dev (sin guard ni DashboardLayout: trae su propio
                shell y sidebar).

                SIGUE detrás de `import.meta.env.DEV`: la pantalla todavía corre con
                datos simulados (nombre, galerías, plan y créditos son constantes), así
                que en producción `/inicio` no existe. Cuando se enchufe a los datos
                reales, sacar el guard es lo único que falta.

                Quedó sólo este estilo (ADR-2026-07-30-queda-solo-fuera-switcher-otras): los otros tres shells y el
                switcher se sacaron del router una vez elegido éste; los archivos
                siguen en `pages/estilos/` por si hay que volver a mirarlos. */}
            {/* `EstiloMinimal` es la ruta de LAYOUT de las tres secciones
                (ADR-2026-07-31-ruta-layout-shell-sidebar-deja): al ser padre común, React lo mantiene montado al navegar
                entre hijas y sólo cambia el `<Outlet />`. Con una ruta por sección
                —cada una montando su propio shell— la sidebar se desmontaba y
                volvía a montar en cada click, y el fade del contenido era
                imposible.

                `/creaciones` y `/editar-marca` van además bajo `ProtectedRoute`
                porque leen datos reales del usuario; `/inicio` corre con datos
                simulados y no lo necesita (ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo). */}
            {import.meta.env.DEV && (
              <Route element={<EstiloMinimal />}>
                <Route path="/inicio" element={<HomeMinimal />} />
                <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
                  <Route path="/creaciones" element={<GeneratedImagesHistory />} />
                  <Route path="/editar-marca" element={<MiMarca />} />
                  {/* Las 8 herramientas de la sidebar, hospedadas (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell): son
                      los MISMOS componentes que sirven `/interfaz-v2/*` y
                      `/editor-video`, montados como hijas de este layout para que
                      conserven la sidebar y el fade de las otras secciones.
                      `/coleccion` no está en la sidebar pero va igual: es a donde
                      llevan los "Ver más" de adentro, y sin ella el flujo se
                      escapaba al shell viejo a mitad de camino. */}
                  {/* La home de V2 hospedada. No está en la sidebar: existe
                      porque es la única pantalla que sabe abrir `?preset=`, y sin
                      ella el click en una plantilla saltaba al shell viejo. */}
                  <Route path="/generar" element={<InterfazV2 />} />
                  <Route path="/modo/:mode" element={<InterfazV2 />} />
                  <Route path="/plantillas" element={<InterfazV2Plantillas />} />
                  <Route path="/coleccion/:slug" element={<ColeccionDetalle />} />
                  <Route path="/estudio-video" element={<VideoProjects />} />
                  {/* Resultados de una generación (ADR-2026-07-31-fail-closed-real-catalogo-enlaces). No está en la sidebar:
                      es a donde caen los ~9 handlers de fin de generación de
                      `InterfazV2`. Sin esta ruta, generar desde `/modo/:mode`
                      terminaba expulsando al shell viejo justo al ver el resultado,
                      que es el momento MENOS oportuno para cambiarle el chrome. */}
                  <Route path="/resultados" element={<GeneratedImagesPage />} />
                  {/* Perfil hospedado (ADR-2026-07-31-embebido-deja-ser-modal-scrim). Es el MISMO `Settings` que sirve
                      `/configuracion`, montado acá para que el shell nuevo tenga su
                      propia ruta y las dos versiones queden separadas: tocar una no
                      arrastra a la otra. `/configuracion` sigue intacta en
                      `DashboardLayout`. */}
                  <Route path="/perfil" element={<Settings />} />
                </Route>
              </Route>
            )}

            {/* Design System v3 — pública, con layout propio (sidebar de carpetas
                anidadas). Sin guard ni DashboardLayout. Ver ADR-671. */}
            <Route path="/design-system" element={<DesignSystemLayout />}>
              <Route index element={<Navigate to={dsFirstPagePath()} replace />} />
              <Route path="getting-started" element={<DSGettingStartedPage />} />
              <Route path="foundations/typography" element={<DSTypographyPage />} />
              <Route path="foundations/colors" element={<DSColorsPage />} />
              <Route path="foundations/spacing" element={<DSSpacingPage />} />
              <Route path="foundations/shadows" element={<DSShadowsPage />} />
              <Route path="foundations/radius" element={<DSRadiusPage />} />
              <Route path="tokens/colors" element={<DSSemanticColorsPage />} />
              <Route path="components/icons" element={<DSIconsPage />} />
            </Route>
            <Route path="/" element={<HomeRedirect />} />

            {import.meta.env.DEV && (
              <Route path="/edicion-grafica-lab" element={<EdicionGraficaLab />} />
            )}
            {/* Todo lo protegido — ProtectedRoute se monta UNA vez y persiste */}
            <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
              {/* Protegidas SIN sidebar (pantalla completa) */}
              <Route path="/checkout" element={<Checkout />} />
              {/* Portal de facturación de la suscripción (solo AR): pantalla propia
                  estilo portal de Stripe, sin el shell de la app. Ver
                  ADR-2026-08-03-facturacion-portal-stripe-completo. */}
              <Route path="/facturacion" element={<FacturacionPortal />} />
              <Route path="/facturacion/factura/:id" element={<FacturaDetalle />} />
              <Route path="/redes-sociales/crear-anuncio" element={<Navigate to="/redes-sociales/crear-anuncio/seleccionar-productos" replace />} />
              <Route path="/redes-sociales/descripcion-brand" element={<BrandDescription />} />
              <Route path="/onboarding/revisar-marca/:brandId" element={<RevisarMarca />} />

              {/* Shell con sidebar — DashboardLayout persiste entre todas estas */}
              <Route element={<DashboardLayout><Outlet /></DashboardLayout>}>
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                <Route path="/registro-ventas" element={<SalesRegister />} />
                <Route path="/historial-ventas" element={<PlaceholderPage title="Historial de ventas" />} />
                <Route path="/envios" element={<PlaceholderPage title="Envíos" />} />
                <Route path="/productos" element={<PriceList />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/descuentos" element={<Discounts />} />
                <Route path="/registro-compras" element={<PlaceholderPage title="Registro de compras" />} />
                <Route path="/historial-compras" element={<PlaceholderPage title="Historial de compras" />} />
                <Route path="/forecasting" element={<PlaceholderPage title="Forecasting" />} />
                <Route path="/proveedores" element={<PlaceholderPage title="Proveedores" />} />
                <Route path="/flujo-caja" element={<PlaceholderPage title="Flujo de caja" />} />
                <Route path="/forecasting-financiero" element={<PlaceholderPage title="Forecasting financiero" />} />
                <Route path="/gastos-recurrentes" element={<PlaceholderPage title="Gastos recurrentes" />} />
                <Route path="/mensajes" element={<Messages />} />
                <Route path="/graficos-de-interaccion" element={<InteractionCharts />} />
                <Route path="/clientes" element={<PlaceholderPage title="Clientes" />} />
                <Route path="/subir-datos" element={<DataUpload />} />
                <Route path="/sincronizacion-automatica" element={<AutomaticSync />} />
                <Route path="/ayuda" element={<Ayuda />} />
                <Route path="/modos" element={<Modos />} />
                <Route path="/mi-marca" element={<MiMarca />} />
                <Route path="/configuracion" element={<Settings />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/subscription" element={<Paywall />} />
                <Route path="/redes-sociales/crear-anuncio/seleccionar-productos" element={<SeleccionarProductosAnuncio />} />
                <Route path="/redes-sociales/crear-anuncio/disenar-video" element={<DisenarVideo />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer-nuevo" element={<ConfigurarVideoInfluencerNuevo />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer-detalle" element={<ConfigurarVideoInfluencerDetalle />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer-video" element={<ConfigurarVideoInfluencerVideo />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer" element={<ConfigurarVideoInfluencer />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-personalizado" element={<ConfigurarVideoPersonalizado />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-cinematografico" element={<ConfigurarVideoCinematografico />} />
                <Route path="/redes-sociales/crear-anuncio/planificador-video-cinematografico" element={<PlanificadorVideoCinematografico />} />
                <Route path="/redes-sociales/video-generado" element={<GeneratedVideoPage />} />
                <Route path="/redes-sociales/crear-anuncio/seleccionar-pose" element={<SeleccionarPoseModelo />} />
                <Route path="/redes-sociales/imagenes" element={<GeneratedImagesHistory />} />
                <Route path="/exportar-tiendanube" element={<ExportarTiendanube />} />
                <Route path="/redes-sociales/editar-imagen" element={<EditarImagen />} />
                <Route path="/redes-sociales/edicion-grafica" element={<EdicionGrafica />} />
                <Route path="/redes-sociales/editar-video" element={<EditarVideo />} />
                <Route path="/redes-sociales/editar-video-ia" element={<EditarVideoIAHistorial />} />
                <Route path="/redes-sociales/imagenes-generadas" element={<GeneratedImagesPage />} />
                <Route path="/redes-sociales/marketing-plan-chat" element={<MarketingPlanChat />} />
                <Route path="/redes-sociales/plan-marketing-view" element={<MarketingPlanReview />} />

                {/* Antes gateadas por BetaTesterRoute (removido: `beta_tester`
                    quedó en desuso con el sistema de grupos y era `true` para
                    todos). Ahora son rutas autenticadas normales; la visibilidad
                    se controla por el nav según el grupo. */}
                <Route path="/test-v1" element={<TestV1 />} />
                <Route path="/interfaz-v2" element={<InterfazV2 />} />
                <Route path="/interfaz-v2/modo/:mode" element={<InterfazV2 />} />
                <Route path="/interfaz-v2/plantillas" element={<InterfazV2Plantillas />} />
                <Route path="/interfaz-v2/coleccion/:slug" element={<ColeccionDetalle />} />
                <Route path="/editor-video" element={<VideoProjects />} />
                <Route path="/editor-video/:projectId" element={<VideoEditor />} />
                <Route path="/redes-sociales/crear-anuncio/prueba-nueva-interfaz" element={<PruebaNuevaInterfaz />} />
                <Route path="/redes-sociales/crear-anuncio/seleccionar-texto-estudio-virtual" element={<SeleccionarTextoEstudioVirtual />} />
                <Route path="/redes-sociales/crear-anuncio/anadir-a-la-escena" element={<AnadirAEscena />} />
                <Route path="/redes-sociales/crear-anuncio/personalizar-modelo-estudio-virtual" element={<PersonalizarModeloEstudioVirtual />} />
                <Route path="/redes-sociales/calendario" element={<CalendarRouter />} />
              </Route>
            </Route>
          </Routes>
          </SidebarProvider>
        </PermissionProvider>
      </Router>
    </MobileBlocker>
  );
}

export default App;